import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, GraduationCap } from "lucide-react";
import { MindMap } from "./MindMap";
import { basicTopics, advancedTopics } from "./data/mindMapData";
import type { Topic } from "./data/mindMapData";
import "./style.css";

type TabKey = "basic" | "advanced";

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "basic", label: "Básico", icon: <BookOpen className="h-4 w-4" /> },
  { key: "advanced", label: "Avançado", icon: <GraduationCap className="h-4 w-4" /> },
];

function EnglishMindmaps(_props: { user: any; setUser: any }) {
  const [activeTab, setActiveTab] = useState<TabKey>("basic");
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);

  return (
    <div
      className={`englishmindmaps-page englishmindmaps-page--fullscreen${selectedTopic ? " englishmindmaps-page--panel-open" : ""}`}
    >
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[900px] w-[900px] rounded-full bg-primary/[0.06] blur-[200px]" />
        <div className="absolute left-1/4 top-1/4 h-[500px] w-[500px] rounded-full bg-accent/[0.04] blur-[150px]" />
        <div className="absolute right-1/4 bottom-1/4 h-[500px] w-[500px] rounded-full bg-secondary/[0.05] blur-[150px]" />
      </div>
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(circle, hsl(270 91% 65%) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
        }}
      />
      {/* Content + MindMap: full height so map goes under transparent app header (no purple bar) */}
      <div className="absolute inset-0 pt-16 sm:pt-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="h-full w-full"
          >
            <MindMap
              topics={activeTab === "basic" ? basicTopics : advancedTopics}
              label={activeTab === "basic" ? "Inglês\nBásico" : "Inglês\nAvançado"}
              selectedTopic={selectedTopic}
              onSelectTopic={setSelectedTopic}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Header (floating) — below app top bar so Básico/Avançado are clickable */}
      <header
        className="pointer-events-none absolute left-0 right-0 z-30 flex items-center justify-start px-3 sm:px-6 py-2"
        style={{ top: "calc(56px + env(safe-area-inset-top, 0px))" }}
      >
        <div className="pointer-events-auto flex items-center gap-1.5 sm:gap-2">
          <div className="flex items-center gap-0.5 sm:gap-1 rounded-full border border-border/40 bg-transparent p-0.5 sm:p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1 sm:gap-1.5 rounded-full px-2.5 sm:px-4 py-1.5 text-[11px] sm:text-sm font-medium transition-all active:scale-95 ${
                  activeTab === tab.key
                    ? "bg-primary text-primary-foreground shadow-md glow-purple"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>
    </div>
  );
}

export default EnglishMindmaps;
